// ---------------------------------------------------------------------------
// strand_graph.ts  —  Phase 1: Strand Graph Engine
//
// Partitions wand-mode strokes at crossing points (knots & junctions) into
// strands (simple curve segments).  Detects loops, computes winding, and
// classifies loop scale.  Built incrementally as the user draws.
//
// Pure data structures + functions.  No rendering, no state singletons.
// ---------------------------------------------------------------------------

import type { StrokePoint } from '../shared/notebook_model.js';

// ── ID types ─────────────────────────────────────────────────────────────────

export type StrandId  = number & { readonly __brand: 'StrandId' };
export type NodeId    = number & { readonly __brand: 'NodeId' };
export type LoopId    = number & { readonly __brand: 'LoopId' };
export type CastStrokeId = number & { readonly __brand: 'CastStrokeId' };

// ── Loop scale ───────────────────────────────────────────────────────────────

export type LoopScale = 'micro' | 'minor' | 'major' | 'grand';

const LOOP_RADIUS_MICRO = 18;
const LOOP_RADIUS_MINOR = 50;
const LOOP_RADIUS_MAJOR = 120;

export function loopScaleFromRadius(r: number): LoopScale {
  if (r < LOOP_RADIUS_MICRO) return 'micro';
  if (r < LOOP_RADIUS_MINOR) return 'minor';
  if (r < LOOP_RADIUS_MAJOR) return 'major';
  return 'grand';
}

// ── Core data structures ─────────────────────────────────────────────────────

export interface Strand {
  id: StrandId;
  /** Ordered points along this strand segment. */
  points: StrokePoint[];
  arcLength: number;
  kind: 'line' | 'arc' | 'loop-segment';
  /** Impedance Z = arcLength / λ. */
  impedance: number;
  fromNode: NodeId;
  toNode: NodeId;
  /** Which cast stroke this strand was carved from. */
  sourceStroke: CastStrokeId;
  /** Phosphor glow values along the strand (1 per ~4px). */
  phosphor: Float32Array;
  /** Net signed flux currently on this strand (Re component of current). */
  flux: number;
  /** Complex current flowing through this strand {re, im}. Updated by field solver. */
  current: { re: number; im: number };
  /** Flow animation phase 0–1, advanced by |current| each tick. */
  flowPhase: number;
}

export interface StrandNode {
  id: NodeId;
  x: number;
  y: number;
  kind: 'knot' | 'junction' | 'endpoint';
  /** IDs of strands connected to this node. */
  strands: StrandId[];
  /** Strand drawn later (on top) at this crossing — for future over/under. */
  over: StrandId | null;
  /** Accumulated flux absorbed at this endpoint/sink. */
  absorbedFlux: number;
  /** Complex potential at this node {re, im}. Updated by field solver. */
  voltage: { re: number; im: number };
}

export interface Loop {
  id: LoopId;
  /** Strand IDs forming the closed path, in order. */
  strands: StrandId[];
  /** Node IDs around the loop, in order. */
  nodes: NodeId[];
  /** +1 = CCW (generative), -1 = CW (consumptive). */
  winding: 1 | -1;
  area: number;
  radius: number;
  scale: LoopScale;
  center: { x: number; y: number };
  /** Is this loop actively emitting motes? */
  active: boolean;
  /** Visual shimmer intensity 0–1. */
  shimmer: number;
  /** Accumulated flux induction from motes passing through (updated by flux engine). */
  induction: number;
}

export interface StrandGraph {
  strands: Map<StrandId, Strand>;
  nodes: Map<NodeId, StrandNode>;
  loops: Loop[];
  /** Monotonic ID counters. */
  _nextStrand: number;
  _nextNode: number;
  _nextLoop: number;
  _nextStroke: number;
  /** Live-construction state (populated during pen-move, cleared on pen-up). */
  _live: LiveState | null;
}

/** Tracks the in-progress stroke during drawing for live construction. */
export interface LiveState {
  /** The growing "preview" strand ID (the un-partitioned tail). */
  strandId: StrandId;
  /** Cast stroke index for hash entries. */
  strokeId: CastStrokeId;
  /** How many points have been registered in the spatial hash. */
  hashUpTo: number;
  /** Strand IDs created by live partitioning (to remove on clearLive). */
  createdStrands: StrandId[];
  /** Node IDs created by live partitioning. */
  createdNodes: NodeId[];
  /** Loop IDs created by live partitioning. */
  createdLoops: LoopId[];
}

// ── Impedance constant ───────────────────────────────────────────────────────

/** Characteristic decay length in px.  Z = arcLen / λ. */
const LAMBDA = 200;

// ── Spatial hash for fast crossing detection ─────────────────────────────────

const CELL = 24; // px per cell

interface SegEntry {
  ax: number; ay: number;
  bx: number; by: number;
  strokeIdx: CastStrokeId;
  segIdx: number;           // index within that stroke's points
}

/**
 * Lightweight spatial hash storing line segments for O(1) lookup of
 * potential crossing candidates.
 */
export class SegmentHash {
  private _cells = new Map<number, SegEntry[]>();

  private _key(cx: number, cy: number): number {
    // Cantor pairing, shifted to handle negatives
    const a = cx + 32768;
    const b = cy + 32768;
    return ((a + b) * (a + b + 1)) / 2 + b;
  }

  /** Cells touched by the AABB of segment (ax,ay)→(bx,by). */
  private _cells_for(ax: number, ay: number, bx: number, by: number): number[] {
    const x0 = Math.floor(Math.min(ax, bx) / CELL);
    const x1 = Math.floor(Math.max(ax, bx) / CELL);
    const y0 = Math.floor(Math.min(ay, by) / CELL);
    const y1 = Math.floor(Math.max(ay, by) / CELL);
    const keys: number[] = [];
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        keys.push(this._key(cx, cy));
      }
    }
    return keys;
  }

  insert(e: SegEntry): void {
    for (const k of this._cells_for(e.ax, e.ay, e.bx, e.by)) {
      let bucket = this._cells.get(k);
      if (!bucket) { bucket = []; this._cells.set(k, bucket); }
      bucket.push(e);
    }
  }

  /** Return segment entries whose AABB overlaps the query segment. */
  query(ax: number, ay: number, bx: number, by: number): SegEntry[] {
    const seen = new Set<number>(); // dedupe by identity
    const result: SegEntry[] = [];
    for (const k of this._cells_for(ax, ay, bx, by)) {
      const bucket = this._cells.get(k);
      if (!bucket) continue;
      for (const e of bucket) {
        const uid = e.strokeIdx * 100000 + e.segIdx;
        if (!seen.has(uid)) { seen.add(uid); result.push(e); }
      }
    }
    return result;
  }

  clear(): void { this._cells.clear(); }
}

// ── Segment intersection ─────────────────────────────────────────────────────

export interface CrossingHit {
  /** Parametric t on segment A (0–1). */
  t: number;
  /** Parametric u on segment B (0–1). */
  u: number;
  /** Intersection point. */
  x: number;
  y: number;
}

/**
 * Exact parametric intersection of (a1→a2) and (b1→b2).
 * Returns null if parallel or intersection outside (ε, 1-ε).
 */
export function segIntersect(
  a1x: number, a1y: number, a2x: number, a2y: number,
  b1x: number, b1y: number, b2x: number, b2y: number,
  eps = 0.02,
): CrossingHit | null {
  const rx = a2x - a1x, ry = a2y - a1y;
  const sx = b2x - b1x, sy = b2y - b1y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-10) return null;
  const qpx = b1x - a1x, qpy = b1y - a1y;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  if (t <= eps || t >= 1 - eps || u <= eps || u >= 1 - eps) return null;
  return {
    t, u,
    x: a1x + t * rx,
    y: a1y + t * ry,
  };
}

// ── Factory helpers ──────────────────────────────────────────────────────────

export function createStrandGraph(): StrandGraph {
  return {
    strands: new Map(),
    nodes: new Map(),
    loops: [],
    _nextStrand: 1,
    _nextNode: 1,
    _nextLoop: 1,
    _nextStroke: 1,
    _live: null,
  };
}

function _makeNodeId(g: StrandGraph): NodeId {
  return (g._nextNode++) as NodeId;
}

function _makeStrandId(g: StrandGraph): StrandId {
  return (g._nextStrand++) as StrandId;
}

function _makeLoopId(g: StrandGraph): LoopId {
  return (g._nextLoop++) as LoopId;
}

// ── Arc length ───────────────────────────────────────────────────────────────

function arcLen(pts: StrokePoint[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    d += Math.sqrt(dx * dx + dy * dy);
  }
  return d;
}

// ── Strand construction ──────────────────────────────────────────────────────

function classifyStrandKind(pts: StrokePoint[]): 'line' | 'arc' | 'loop-segment' {
  if (pts.length < 3) return 'line';
  const first = pts[0], last = pts[pts.length - 1];
  const dist = Math.sqrt((last.x - first.x) ** 2 + (last.y - first.y) ** 2);
  const len = arcLen(pts);
  if (len < 1) return 'line';
  const straight = dist / len;
  if (straight > 0.85) return 'line';
  // Check if endpoints are the same node (loop segment)
  if (dist < 4 && len > 20) return 'loop-segment';
  return 'arc';
}

function buildStrand(
  g: StrandGraph,
  pts: StrokePoint[],
  fromNode: NodeId,
  toNode: NodeId,
  sourceStroke: CastStrokeId,
): Strand {
  const id = _makeStrandId(g);
  const al = arcLen(pts);
  const phosphorLen = Math.max(1, Math.ceil(al / 4));
  const strand: Strand = {
    id,
    points: pts,
    arcLength: al,
    kind: classifyStrandKind(pts),
    impedance: al / LAMBDA,
    fromNode,
    toNode,
    sourceStroke,
    phosphor: new Float32Array(phosphorLen),
    flux: 0,
    current: { re: 0, im: 0 },
    flowPhase: 0,
  };
  g.strands.set(id, strand);

  // Connect nodes
  const fn = g.nodes.get(fromNode);
  if (fn && !fn.strands.includes(id)) fn.strands.push(id);
  const tn = g.nodes.get(toNode);
  if (tn && !tn.strands.includes(id)) tn.strands.push(id);

  return strand;
}

function buildNode(
  g: StrandGraph,
  x: number, y: number,
  kind: 'knot' | 'junction' | 'endpoint',
): StrandNode {
  const id = _makeNodeId(g);
  const node: StrandNode = { id, x, y, kind, strands: [], over: null, absorbedFlux: 0, voltage: { re: 0, im: 0 } };
  g.nodes.set(id, node);
  return node;
}

// ── Shoelace winding ─────────────────────────────────────────────────────────

/** Signed area of polygon via shoelace.  Positive = CCW. */
function shoelaceArea(pts: { x: number; y: number }[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    a += (pts[j].x - pts[i].x) * (pts[j].y + pts[i].y);
  }
  return a / 2; // positive = CW in screen coords (Y-down) → we invert for math convention
}

/**
 * Compute winding from strand points around a closed loop.
 * Screen coords: Y-down, so shoelace positive = CW screen = CW.
 * We define CCW-screen (pen moving counter-clockwise visually) as +1.
 * In Y-down screen coords, visual CCW gives negative shoelace, so:
 *   winding = shoelace < 0 ? +1 : -1
 */
function loopWinding(loopPts: { x: number; y: number }[]): 1 | -1 {
  return shoelaceArea(loopPts) < 0 ? 1 : -1;
}

// ── Loop detection ───────────────────────────────────────────────────────────

/**
 * Given a node where a crossing was just created, walk the graph to find
 * any newly formed closed path.  Returns the loop's node sequence or null.
 *
 * Uses BFS limited to depth 8 (loops with >8 strands are too complex).
 */
function findNewLoop(g: StrandGraph, startNode: NodeId): NodeId[] | null {
  const MAX_DEPTH = 8;

  interface QEntry { node: NodeId; path: NodeId[]; visited: Set<number>; }

  const queue: QEntry[] = [];
  const startN = g.nodes.get(startNode)!;

  // Seed: walk one step from start along each connected strand
  for (const sid of startN.strands) {
    const strand = g.strands.get(sid)!;
    const next = strand.fromNode === startNode ? strand.toNode : strand.fromNode;
    if (next === startNode) {
      // Self-loop (strand from a node back to itself)
      return [startNode];
    }
    const visited = new Set<number>();
    visited.add(startNode as number);
    visited.add(next as number);
    queue.push({ node: next, path: [startNode, next], visited });
  }

  while (queue.length > 0) {
    const { node, path, visited } = queue.shift()!;
    if (path.length > MAX_DEPTH) continue;

    const n = g.nodes.get(node)!;
    for (const sid of n.strands) {
      const strand = g.strands.get(sid)!;
      const next = strand.fromNode === node ? strand.toNode : strand.fromNode;

      if (next === startNode && path.length >= 3) {
        // Found a cycle back to start
        return path;
      }

      if (visited.has(next as number)) continue;

      const newVisited = new Set(visited);
      newVisited.add(next as number);
      queue.push({ node: next, path: [...path, next], visited: newVisited });
    }
  }

  return null;
}

/**
 * Create a Loop from a cycle of node IDs.  Collects the strands between
 * consecutive nodes, computes polygon, winding, area, scale.
 */
function buildLoop(g: StrandGraph, nodeCycle: NodeId[]): Loop | null {
  const strands: StrandId[] = [];
  // Walk pairs of consecutive nodes in the cycle
  for (let i = 0; i < nodeCycle.length; i++) {
    const a = nodeCycle[i];
    const b = nodeCycle[(i + 1) % nodeCycle.length];
    // Find strand connecting a↔b
    const nodeA = g.nodes.get(a)!;
    let found: StrandId | null = null;
    for (const sid of nodeA.strands) {
      const s = g.strands.get(sid)!;
      if ((s.fromNode === a && s.toNode === b) || (s.fromNode === b && s.toNode === a)) {
        found = sid;
        break;
      }
    }
    if (found === null) return null; // broken path
    strands.push(found);
  }

  // Collect ordered polygon points from strands
  const polyPts: { x: number; y: number }[] = [];
  for (let i = 0; i < strands.length; i++) {
    const strand = g.strands.get(strands[i])!;
    const expectedFrom = nodeCycle[i];
    const pts = strand.fromNode === expectedFrom
      ? strand.points
      : [...strand.points].reverse();
    // Add all points except the last (it's the next strand's first)
    for (let j = 0; j < pts.length - 1; j++) {
      polyPts.push(pts[j]);
    }
  }

  if (polyPts.length < 3) return null;

  const signedArea = shoelaceArea(polyPts);
  const area = Math.abs(signedArea);
  const winding = loopWinding(polyPts);
  const radius = Math.sqrt(area / Math.PI);
  const scale = loopScaleFromRadius(radius);

  // Centroid
  let cx = 0, cy = 0;
  for (const p of polyPts) { cx += p.x; cy += p.y; }
  cx /= polyPts.length;
  cy /= polyPts.length;

  const id = _makeLoopId(g);
  return {
    id, strands, nodes: nodeCycle, winding, area, radius, scale,
    center: { x: cx, y: cy },
    active: true,
    shimmer: 0,
    induction: 0,
  };
}

// ── Self-crossing detection within a single stroke ───────────────────────────

interface InternalCrossing {
  /** Index in the stroke's point array where crossing occurs on the earlier segment. */
  idxA: number;
  /** Index on the later segment. */
  idxB: number;
  /** Exact intersection point. */
  x: number;
  y: number;
  /** Parametric position on segment A (for sub-point precision). */
  tA: number;
  /** Parametric position on segment B. */
  tB: number;
}

/**
 * Find all self-crossings in a stroke.  Skip adjacent segments.
 * Returns crossings sorted by idxA ascending.
 */
function findSelfCrossings(pts: StrokePoint[], skip = 3): InternalCrossing[] {
  const crossings: InternalCrossing[] = [];
  for (let i = skip; i < pts.length - 1; i++) {
    for (let j = 0; j < i - skip; j++) {
      const hit = segIntersect(
        pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y,
        pts[j].x, pts[j].y, pts[j + 1].x, pts[j + 1].y,
      );
      if (hit) {
        crossings.push({
          idxA: j, idxB: i,
          x: hit.x, y: hit.y,
          tA: hit.u, tB: hit.t, // note: seg A is j..j+1 = B in segIntersect call
        });
      }
    }
  }
  crossings.sort((a, b) => a.idxA - b.idxA || a.idxB - b.idxB);
  return crossings;
}

// ── Inter-stroke crossing detection ──────────────────────────────────────────

interface InterCrossing {
  strokeA: CastStrokeId;
  segA: number;
  strokeB: CastStrokeId;
  segB: number;
  x: number;
  y: number;
  tA: number;
  tB: number;
}

function findInterCrossings(
  hash: SegmentHash,
  strokeIdx: CastStrokeId,
  pts: StrokePoint[],
): InterCrossing[] {
  const crossings: InterCrossing[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const ax = pts[i].x, ay = pts[i].y;
    const bx = pts[i + 1].x, by = pts[i + 1].y;
    const candidates = hash.query(ax, ay, bx, by);
    for (const c of candidates) {
      // Skip same stroke (self-crossings handled separately)
      if (c.strokeIdx === strokeIdx) continue;
      const hit = segIntersect(ax, ay, bx, by, c.ax, c.ay, c.bx, c.by);
      if (hit) {
        crossings.push({
          strokeA: strokeIdx,
          segA: i,
          strokeB: c.strokeIdx,
          segB: c.segIdx,
          x: hit.x, y: hit.y,
          tA: hit.t, tB: hit.u,
        });
      }
    }
  }
  return crossings;
}

// ── Interpolate a StrokePoint at a fractional segment index ──────────────────

function lerpPoint(pts: StrokePoint[], idx: number, t: number): StrokePoint {
  const a = pts[idx];
  const b = pts[Math.min(idx + 1, pts.length - 1)];
  return {
    x: a.x + t * (b.x - a.x),
    y: a.y + t * (b.y - a.y),
    pressure: a.pressure + t * (b.pressure - a.pressure),
    tiltX: a.tiltX + t * (b.tiltX - a.tiltX),
    tiltY: a.tiltY + t * (b.tiltY - a.tiltY),
    twist: a.twist + t * (b.twist - a.twist),
    timestamp: a.timestamp + t * (b.timestamp - a.timestamp),
  };
}

// ── Add a stroke to the graph (main entry point) ────────────────────────────

export interface AddStrokeResult {
  strokeId: CastStrokeId;
  /** Newly created loops (if any crossings formed closed paths). */
  newLoops: Loop[];
  /** All newly created strand IDs. */
  newStrands: StrandId[];
  /** All newly created node IDs. */
  newNodes: NodeId[];
  /** Endpoint node at the stroke's first point (source for wand energy). */
  startNodeId: NodeId;
  /** Endpoint node at the stroke's last point (sink for wand energy). */
  endNodeId: NodeId;
}

/**
 * Add a complete stroke to the strand graph.  Finds self-crossings and
 * inter-stroke crossings, partitions the stroke into strands at crossing
 * points, creates nodes, detects new loops.
 *
 * This is the incremental build function — call once per pen-up or once
 * per detected crossing during live drawing.
 */
export function addStroke(
  g: StrandGraph,
  pts: StrokePoint[],
  hash: SegmentHash,
): AddStrokeResult {
  if (pts.length < 2) {
    return { strokeId: 0 as CastStrokeId, newLoops: [], newStrands: [], newNodes: [], startNodeId: 0 as NodeId, endNodeId: 0 as NodeId };
  }

  const strokeIdx = g._nextStroke++ as CastStrokeId;
  const result: AddStrokeResult = { strokeId: strokeIdx, newLoops: [], newStrands: [], newNodes: [], startNodeId: 0 as NodeId, endNodeId: 0 as NodeId };

  // 1. Find self-crossings
  const selfX = findSelfCrossings(pts);

  // 2. Find inter-stroke crossings (against existing strokes in hash)
  const interX = findInterCrossings(hash, strokeIdx, pts);

  // 3. Merge into a unified list of cut points on this stroke, sorted by position
  interface CutPoint {
    /** Segment index in original stroke. */
    segIdx: number;
    /** Parametric t within that segment. */
    t: number;
    /** Intersection position. */
    x: number;
    y: number;
    /** If this crossing also cuts another stroke, record it. */
    otherStroke: CastStrokeId | null;
    otherSeg: number;
    otherT: number;
  }

  const cuts: CutPoint[] = [];

  for (const sx of selfX) {
    // Self-crossing creates two cut points: one on the earlier segment, one on the later
    cuts.push({ segIdx: sx.idxA, t: sx.tA, x: sx.x, y: sx.y, otherStroke: null, otherSeg: sx.idxB, otherT: sx.tB });
    cuts.push({ segIdx: sx.idxB, t: sx.tB, x: sx.x, y: sx.y, otherStroke: null, otherSeg: sx.idxA, otherT: sx.tA });
  }

  for (const ix of interX) {
    cuts.push({ segIdx: ix.segA, t: ix.tA, x: ix.x, y: ix.y, otherStroke: ix.strokeB, otherSeg: ix.segB, otherT: ix.tB });
  }

  // Sort by position along stroke
  cuts.sort((a, b) => a.segIdx - b.segIdx || a.t - b.t);

  // Deduplicate cuts that are very close along the stroke.
  // Two cuts at the same (x,y) but far apart in parametric position (segIdx)
  // are NOT duplicates — they're the two sides of a self-crossing and both
  // are needed to properly partition the stroke around a loop.
  const dedupedCuts: CutPoint[] = [];
  for (const c of cuts) {
    const last = dedupedCuts[dedupedCuts.length - 1];
    if (last && Math.abs(c.x - last.x) < 2 && Math.abs(c.y - last.y) < 2
        && Math.abs(c.segIdx - last.segIdx) < 2) continue;
    dedupedCuts.push(c);
  }

  // 4. Create crossing nodes for each unique intersection point
  //    Build a map: crossing position → NodeId
  const crossingNodes: { cut: CutPoint; nodeId: NodeId }[] = [];

  // We also need to handle the case where a self-crossing creates ONE node
  // (both cut points map to the same spatial position)
  const posKey = (x: number, y: number) => `${Math.round(x)},${Math.round(y)}`;
  const posToNode = new Map<string, NodeId>();

  for (const c of dedupedCuts) {
    const key = posKey(c.x, c.y);
    let nodeId = posToNode.get(key);
    if (!nodeId) {
      const kind = c.otherStroke !== null ? 'junction' : 'knot';
      const node = buildNode(g, c.x, c.y, kind);
      nodeId = node.id;
      posToNode.set(key, nodeId);
      result.newNodes.push(nodeId);
    }
    crossingNodes.push({ cut: c, nodeId });
  }

  // 5. Partition stroke into strands at cut points
  //    Create endpoint nodes for the stroke's start and end.
  const startNode = buildNode(g, pts[0].x, pts[0].y, 'endpoint');
  const endNode = buildNode(g, pts[pts.length - 1].x, pts[pts.length - 1].y, 'endpoint');
  result.newNodes.push(startNode.id, endNode.id);
  result.startNodeId = startNode.id;
  result.endNodeId = endNode.id;

  // Build ordered list of (node, point-index) pairs along the stroke
  interface StrandBoundary {
    nodeId: NodeId;
    point: StrokePoint;
    /** Index in original points array (fractional = interpolated). */
    approxIdx: number;
  }

  const boundaries: StrandBoundary[] = [
    { nodeId: startNode.id, point: pts[0], approxIdx: 0 },
  ];

  for (const cn of crossingNodes) {
    const crossPt = lerpPoint(pts, cn.cut.segIdx, cn.cut.t);
    boundaries.push({
      nodeId: cn.nodeId,
      point: crossPt,
      approxIdx: cn.cut.segIdx + cn.cut.t,
    });
  }

  boundaries.push({
    nodeId: endNode.id,
    point: pts[pts.length - 1],
    approxIdx: pts.length - 1,
  });

  // Sort by position along stroke (should already be, but ensure)
  boundaries.sort((a, b) => a.approxIdx - b.approxIdx);

  // 6. Create strands between consecutive boundaries
  for (let i = 0; i < boundaries.length - 1; i++) {
    const from = boundaries[i];
    const to = boundaries[i + 1];

    // Extract sub-array of points for this strand
    const startIdx = Math.ceil(from.approxIdx);
    const endIdx = Math.floor(to.approxIdx);

    const strandPts: StrokePoint[] = [from.point];
    for (let j = startIdx; j <= endIdx && j < pts.length; j++) {
      strandPts.push(pts[j]);
    }
    // Add the boundary point at end if different from last
    if (strandPts.length === 0 || to.approxIdx !== endIdx) {
      strandPts.push(to.point);
    }

    if (strandPts.length < 2) continue;

    const strand = buildStrand(g, strandPts, from.nodeId, to.nodeId, strokeIdx);
    result.newStrands.push(strand.id);
  }

  // 7. Handle inter-stroke crossings: split existing strands that were cut
  for (const ix of interX) {
    splitExistingStrand(g, ix, strokeIdx, result);
  }

  // 8. Insert this stroke's segments into the spatial hash
  for (let i = 0; i < pts.length - 1; i++) {
    hash.insert({
      ax: pts[i].x, ay: pts[i].y,
      bx: pts[i + 1].x, by: pts[i + 1].y,
      strokeIdx,
      segIdx: i,
    });
  }

  // 9. Detect new loops from any crossing nodes created
  for (const cn of crossingNodes) {
    const cycle = findNewLoop(g, cn.nodeId);
    if (cycle) {
      // Check we haven't already registered this loop
      const cycleKey = [...cycle].sort((a, b) => (a as number) - (b as number)).join(',');
      const exists = g.loops.some(l => {
        const lk = [...l.nodes].sort((a, b) => (a as number) - (b as number)).join(',');
        return lk === cycleKey;
      });
      if (!exists) {
        const loop = buildLoop(g, cycle);
        if (loop) {
          g.loops.push(loop);
          result.newLoops.push(loop);
        }
      }
    }
  }

  return result;
}

// ── Split an existing strand at an inter-stroke crossing ─────────────────────

function splitExistingStrand(
  g: StrandGraph,
  ix: InterCrossing,
  newStrokeIdx: CastStrokeId,
  result: AddStrokeResult,
): void {
  // Find the strand that contains this segment of the other stroke
  // We need to find a strand from ix.strokeB that contains segIdx ix.segB
  let targetStrand: Strand | null = null;
  for (const [, s] of g.strands) {
    if (s.sourceStroke !== ix.strokeB) continue;
    // Check if this strand's points span the crossing segment
    // Simple check: the crossing point should be close to the strand
    const dx = ix.x - s.points[0].x;
    const dy = ix.y - s.points[0].y;
    const dx2 = ix.x - s.points[s.points.length - 1].x;
    const dy2 = ix.y - s.points[s.points.length - 1].y;
    // Check if crossing point is within the strand's bbox
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of s.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    if (ix.x >= minX - 4 && ix.x <= maxX + 4 && ix.y >= minY - 4 && ix.y <= maxY + 4) {
      targetStrand = s;
      break;
    }
  }

  if (!targetStrand) return;

  // Find the closest point in the strand to the crossing
  let bestDist = Infinity;
  let splitIdx = 0;
  for (let i = 0; i < targetStrand.points.length; i++) {
    const d = (targetStrand.points[i].x - ix.x) ** 2 + (targetStrand.points[i].y - ix.y) ** 2;
    if (d < bestDist) { bestDist = d; splitIdx = i; }
  }

  if (splitIdx < 1 || splitIdx >= targetStrand.points.length - 1) return;

  // Get or create the junction node at the crossing point
  const posK = `${Math.round(ix.x)},${Math.round(ix.y)}`;
  let junctionNode: StrandNode | null = null;
  for (const [, n] of g.nodes) {
    if (Math.abs(n.x - ix.x) < 3 && Math.abs(n.y - ix.y) < 3 && n.kind !== 'endpoint') {
      junctionNode = n;
      break;
    }
  }
  if (!junctionNode) {
    junctionNode = buildNode(g, ix.x, ix.y, 'junction');
    result.newNodes.push(junctionNode.id);
  }

  // Mark new stroke's strand as "over" (drawn later)
  junctionNode.over = result.newStrands.length > 0 ? result.newStrands[0] : null;

  // Split the target strand into two
  const ptsA = targetStrand.points.slice(0, splitIdx + 1);
  const ptsB = targetStrand.points.slice(splitIdx);

  const oldFrom = targetStrand.fromNode;
  const oldTo = targetStrand.toNode;

  // Remove old strand from its nodes
  const fromN = g.nodes.get(oldFrom);
  const toN = g.nodes.get(oldTo);
  if (fromN) fromN.strands = fromN.strands.filter(s => s !== targetStrand!.id);
  if (toN) toN.strands = toN.strands.filter(s => s !== targetStrand!.id);

  // Delete old strand
  g.strands.delete(targetStrand.id);

  // Build two new strands
  if (ptsA.length >= 2) {
    const s1 = buildStrand(g, ptsA, oldFrom, junctionNode.id, targetStrand.sourceStroke);
    result.newStrands.push(s1.id);
  }
  if (ptsB.length >= 2) {
    const s2 = buildStrand(g, ptsB, junctionNode.id, oldTo, targetStrand.sourceStroke);
    result.newStrands.push(s2.id);
  }
}

// ── Query helpers ────────────────────────────────────────────────────────────

/** Get all endpoint nodes (degree 1, no further connections). */
export function getEndpoints(g: StrandGraph): StrandNode[] {
  const result: StrandNode[] = [];
  for (const [, n] of g.nodes) {
    if (n.kind === 'endpoint') result.push(n);
  }
  return result;
}

/** Get all nodes that are crossings (knot or junction). */
export function getCrossingNodes(g: StrandGraph): StrandNode[] {
  const result: StrandNode[] = [];
  for (const [, n] of g.nodes) {
    if (n.kind === 'knot' || n.kind === 'junction') result.push(n);
  }
  return result;
}

/** Given a node, return the other end(s) of its connected strands. */
export function neighbours(g: StrandGraph, nodeId: NodeId): { node: StrandNode; strand: Strand }[] {
  const n = g.nodes.get(nodeId);
  if (!n) return [];
  const result: { node: StrandNode; strand: Strand }[] = [];
  for (const sid of n.strands) {
    const s = g.strands.get(sid)!;
    const otherId = s.fromNode === nodeId ? s.toNode : s.fromNode;
    const other = g.nodes.get(otherId);
    if (other) result.push({ node: other, strand: s });
  }
  return result;
}

/** Collect the polygon points for a loop (for rendering / containment). */
export function loopPolygon(g: StrandGraph, loop: Loop): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < loop.strands.length; i++) {
    const strand = g.strands.get(loop.strands[i])!;
    const expectedFrom = loop.nodes[i];
    const points = strand.fromNode === expectedFrom
      ? strand.points
      : [...strand.points].reverse();
    for (let j = 0; j < points.length - 1; j++) {
      pts.push(points[j]);
    }
  }
  return pts;
}

/** Check if a point is inside a loop polygon (ray casting). */
export function pointInLoop(
  g: StrandGraph,
  loop: Loop,
  px: number, py: number,
): boolean {
  const poly = loopPolygon(g, loop);
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Check if a grand loop has rectangular shape (4 dominant corners). */
export function isRectangularLoop(g: StrandGraph, loop: Loop): boolean {
  if (loop.scale !== 'grand') return false;
  const poly = loopPolygon(g, loop);
  if (poly.length < 8) return false;

  // Find corners via angle change
  const corners: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const prev = poly[(i - 1 + poly.length) % poly.length];
    const curr = poly[i];
    const next = poly[(i + 1) % poly.length];
    const a1 = Math.atan2(curr.y - prev.y, curr.x - prev.x);
    const a2 = Math.atan2(next.y - curr.y, next.x - curr.x);
    let da = Math.abs(a2 - a1);
    if (da > Math.PI) da = 2 * Math.PI - da;
    if (da > 0.8) corners.push(i); // ~45° threshold
  }

  if (corners.length < 4 || corners.length > 6) return false;

  // Check that corners are roughly 90°
  let rightAngleCount = 0;
  for (const ci of corners) {
    const prev = poly[(ci - 3 + poly.length) % poly.length];
    const curr = poly[ci];
    const next = poly[(ci + 3) % poly.length];
    const a1 = Math.atan2(curr.y - prev.y, curr.x - prev.x);
    const a2 = Math.atan2(next.y - curr.y, next.x - curr.x);
    let da = Math.abs(a2 - a1);
    if (da > Math.PI) da = 2 * Math.PI - da;
    if (da > 1.1 && da < 1.9) rightAngleCount++; // 63°–109°
  }

  return rightAngleCount >= 3;
}

/** Total strand count. */
export function strandCount(g: StrandGraph): number {
  return g.strands.size;
}

/** Total node count. */
export function nodeCount(g: StrandGraph): number {
  return g.nodes.size;
}

/** Reset the graph completely. */
export function clearGraph(g: StrandGraph): void {
  g.strands.clear();
  g.nodes.clear();
  g.loops.length = 0;
  g._nextStrand = 1;
  g._nextNode = 1;
  g._nextLoop = 1;
  g._nextStroke = 1;
  g._live = null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIVE CONSTRUCTION — Incremental graph building during pen-move
// ═══════════════════════════════════════════════════════════════════════════════

const LIVE_SKIP = 3;            // min segment gap for self-crossing check
const LIVE_PHOSPHOR_BASE = 0.3; // base phosphor glow for live strands

/**
 * Begin live construction for a new stroke.  Call once on pen-down (after
 * graph reset for fresh casts).  Creates the initial live strand.
 */
export function beginLiveStroke(
  g: StrandGraph,
  firstPt: StrokePoint,
  hash: SegmentHash,
): void {
  const strokeId = g._nextStroke++ as CastStrokeId;

  // Endpoint node at stroke start
  const startNode = buildNode(g, firstPt.x, firstPt.y, 'endpoint');

  // Temporary end node (will move with the pen)
  const endNode = buildNode(g, firstPt.x, firstPt.y, 'endpoint');

  // The growing strand
  const strandId = _makeStrandId(g);
  const strand: Strand = {
    id: strandId,
    points: [firstPt],
    arcLength: 0,
    kind: 'line',
    impedance: 0,
    fromNode: startNode.id,
    toNode: endNode.id,
    sourceStroke: strokeId,
    phosphor: new Float32Array(1),
    flux: 0,
    current: { re: 0, im: 0 },
    flowPhase: 0,
  };
  strand.phosphor[0] = LIVE_PHOSPHOR_BASE;
  g.strands.set(strandId, strand);
  startNode.strands.push(strandId);
  endNode.strands.push(strandId);

  g._live = {
    strandId,
    strokeId,
    hashUpTo: 0,
    createdStrands: [strandId],
    createdNodes: [startNode.id, endNode.id],
    createdLoops: [],
  };
}

export interface LiveFeedResult {
  /** Loops created this feed (empty most of the time). */
  newLoops: Loop[];
}

/**
 * Feed a new point to the live strand.  Call on every pen-move sample.
 * Updates the growing strand, registers segments in the spatial hash,
 * and checks for self-crossings.  Returns any newly-detected loops.
 *
 * Cost: O(n/cell) per point for hash-based self-crossing.  For typical
 * strokes (~200–600 points) this is well within budget.
 */
export function feedLivePoint(
  g: StrandGraph,
  pt: StrokePoint,
  allPts: StrokePoint[],
  hash: SegmentHash,
): LiveFeedResult {
  const live = g._live;
  if (!live) return { newLoops: [] };

  const strand = g.strands.get(live.strandId)!;

  // Spatial decimation — skip points < 2px from the last accepted point.
  // Prevents O(n²) self-crossing checks from choking high-frequency pens.
  const n0 = strand.points.length;
  if (n0 >= 1) {
    const prev = strand.points[n0 - 1];
    const dx = pt.x - prev.x, dy = pt.y - prev.y;
    if (dx * dx + dy * dy < 4) {
      // Still move the end node so live preview is smooth
      const endNode = g.nodes.get(strand.toNode);
      if (endNode) { endNode.x = pt.x; endNode.y = pt.y; }
      return { newLoops: [] };
    }
  }

  // 1. Append point to the live strand
  strand.points.push(pt);
  const n = strand.points.length;

  // Update arc length
  if (n >= 2) {
    const prev = strand.points[n - 2];
    const dx = pt.x - prev.x, dy = pt.y - prev.y;
    strand.arcLength += Math.sqrt(dx * dx + dy * dy);
    strand.impedance = strand.arcLength / LAMBDA;
  }

  // Grow phosphor array and fill with base glow
  const neededPh = Math.max(1, Math.ceil(strand.arcLength / 4));
  if (neededPh > strand.phosphor.length) {
    const newPh = new Float32Array(neededPh);
    newPh.set(strand.phosphor);
    for (let i = strand.phosphor.length; i < neededPh; i++) {
      newPh[i] = LIVE_PHOSPHOR_BASE;
    }
    strand.phosphor = newPh;
  }

  // Move the live end-node to the pen position
  const endNode = g.nodes.get(strand.toNode);
  if (endNode) { endNode.x = pt.x; endNode.y = pt.y; }

  // 2. Register new segments in the hash (for inter-stroke detection later)
  //    We register using the FULL allPts array (same points as strand.points
  //    for a fresh stroke, but allPts is the canonical source).
  const ptIdx = allPts.length - 1; // index of the new point in allPts
  if (ptIdx >= 1) {
    const prev = allPts[ptIdx - 1];
    hash.insert({
      ax: prev.x, ay: prev.y,
      bx: pt.x, by: pt.y,
      strokeIdx: live.strokeId,
      segIdx: ptIdx - 1,
    });
    live.hashUpTo = ptIdx;
  }

  // 3. Self-crossing check: test the newest segment against earlier segments
  //    Use the hash for efficiency — query the spatial region of the new segment.
  const result: LiveFeedResult = { newLoops: [] };
  if (n < LIVE_SKIP + 2) return result;

  const segIdx = n - 2; // index of the new segment in strand.points
  const ax = strand.points[segIdx].x, ay = strand.points[segIdx].y;
  const bx = strand.points[segIdx + 1].x, by = strand.points[segIdx + 1].y;

  // Check against ALL earlier segments of THIS stroke (skip adjacent)
  for (let j = 0; j < segIdx - LIVE_SKIP; j++) {
    const p0 = strand.points[j], p1 = strand.points[j + 1];
    const hit = segIntersect(ax, ay, bx, by, p0.x, p0.y, p1.x, p1.y);
    if (!hit) continue;

    // Found a self-crossing → partition the stroke at this crossing and
    // create a loop from the enclosed region.
    const crossX = hit.x, crossY = hit.y;

    // Create a knot node at the crossing
    const knotNode = buildNode(g, crossX, crossY, 'knot');
    live.createdNodes.push(knotNode.id);

    // Extract the loop region: points from j..segIdx+1 form the loop
    // (the region between the two crossing points on the same stroke).
    const loopStartT = hit.u; // parametric on the earlier segment
    const loopEndT = hit.t;   // parametric on the later segment

    const loopPts: StrokePoint[] = [];
    // Start with interpolated point at crossing on segment j
    loopPts.push(lerpPoint(strand.points, j, loopStartT));
    // All full points between j+1 and segIdx (inclusive)
    for (let k = j + 1; k <= segIdx; k++) {
      loopPts.push(strand.points[k]);
    }
    // End with interpolated point at crossing on latest segment
    loopPts.push(lerpPoint(strand.points, segIdx, loopEndT));

    if (loopPts.length < 3) continue;

    // Build a loop strand (knot back to itself)
    const loopStrandId = _makeStrandId(g);
    const al = arcLen(loopPts);
    const phLen = Math.max(1, Math.ceil(al / 4));
    const ph = new Float32Array(phLen);
    ph.fill(LIVE_PHOSPHOR_BASE);
    const loopStrand: Strand = {
      id: loopStrandId,
      points: loopPts,
      arcLength: al,
      kind: 'loop-segment',
      impedance: al / LAMBDA,
      fromNode: knotNode.id,
      toNode: knotNode.id,
      sourceStroke: live.strokeId,
      phosphor: ph,
      flux: 0,
      current: { re: 0, im: 0 },
      flowPhase: 0,
    };
    g.strands.set(loopStrandId, loopStrand);
    knotNode.strands.push(loopStrandId);
    live.createdStrands.push(loopStrandId);

    // Build the loop directly from the polygon
    const signedArea = shoelaceArea(loopPts);
    const area = Math.abs(signedArea);
    const winding = loopWinding(loopPts);
    const radius = Math.sqrt(area / Math.PI);
    const scale = loopScaleFromRadius(radius);

    let cx = 0, cy = 0;
    for (const p of loopPts) { cx += p.x; cy += p.y; }
    cx /= loopPts.length; cy /= loopPts.length;

    const loopId = _makeLoopId(g);
    const loop: Loop = {
      id: loopId,
      strands: [loopStrandId],
      nodes: [knotNode.id],
      winding, area, radius, scale,
      center: { x: cx, y: cy },
      active: true,
      shimmer: 0,
      induction: 0,
    };
    g.loops.push(loop);
    live.createdLoops.push(loopId);
    result.newLoops.push(loop);

    // Only process the first crossing found per feed (keeps it simple)
    break;
  }

  return result;
}

/**
 * Clear all live-construction state from the graph.  Call on pen-up
 * BEFORE calling addStroke() for the finalized stroke — addStroke will
 * rebuild everything properly.
 */
export function clearLiveStrand(g: StrandGraph, hash: SegmentHash): void {
  const live = g._live;
  if (!live) return;

  // Remove live strands, nodes, loops
  for (const sid of live.createdStrands) g.strands.delete(sid);
  for (const nid of live.createdNodes) g.nodes.delete(nid);
  for (const lid of live.createdLoops) {
    const idx = g.loops.findIndex(l => l.id === lid);
    if (idx >= 0) g.loops.splice(idx, 1);
  }

  // Clear hash entries for this stroke (rebuild from scratch in addStroke)
  // Since SegmentHash doesn't support selective removal, we clear and
  // re-insert entries from previous (finalized) strokes.
  // For now, since live is only one stroke within a cast, this is safe:
  // the hash was populated only by live segments, and addStroke will re-register.
  hash.clear();

  // Re-register any previously-finalized strands in the hash
  // (from earlier strokes in a multi-stroke cast)
  for (const [, strand] of g.strands) {
    const pts = strand.points;
    for (let i = 0; i < pts.length - 1; i++) {
      hash.insert({
        ax: pts[i].x, ay: pts[i].y,
        bx: pts[i + 1].x, by: pts[i + 1].y,
        strokeIdx: strand.sourceStroke,
        segIdx: i,
      });
    }
  }

  g._live = null;
}
